import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { User, Briefcase, GraduationCap, FolderLock, IdCard, Plus, Trash2, Upload, Camera } from 'lucide-react';
import { roleLabel } from '@/lib/roleLabels';

const TABS = [
  ['personal', 'Personal Details', IdCard],
  ['profile', 'My Profile', User],
  ['employment', 'Employment Information', Briefcase],
  ['qualifications', 'Qualifications', GraduationCap],
  ['documents', 'Employment Documents', FolderLock],
];

function ReadOnly({ label, value, testId }) {
  return (
    <div className="space-y-1">
      <div className="text-xs text-muted-foreground flex items-center gap-1.5">{label}
        <Badge variant="outline" className="text-[9px] px-1 py-0">Read only</Badge>
      </div>
      <div className="text-sm text-foreground min-h-[20px]" data-testid={testId}>{value || <span className="text-muted-foreground">Not recorded</span>}</div>
    </div>
  );
}

function errMsg(e, fallback) {
  const data = e?.response?.data;
  const d = data?.detail;
  return (
    e?.userMessage
    || d?.error?.message || d?.message || (typeof d === 'string' ? d : null)
    || data?.error?.message || data?.message || (typeof data === 'string' ? data : null)
  ) || fallback;
}

function PersonalTab({ data, version, reload }) {
  const p = data.personal;
  const [form, setForm] = useState({
    preferred_name: p.preferred_name || '', phone: p.phone || '', personal_email: p.personal_email || '',
    address: { line1: '', line2: '', suburb: '', state: '', postcode: '', ...(p.address || {}) },
    emergency_contacts: p.emergency_contacts || [],
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put('/staff/me/personal', { ...form, version });
      toast.success('Personal details saved');
      reload();
    } catch (e) {
      if (e?.response?.status === 409) { toast.error('Your details changed elsewhere — refreshing'); reload(); }
      else toast.error(errMsg(e, 'Unable to save'));
    } finally { setBusy(false); }
  };
  const setEc = (i, k, v) => setForm((f) => ({ ...f, emergency_contacts: f.emergency_contacts.map((c, j) => (j === i ? { ...c, [k]: v } : c)) }));
  return (
    <div className="space-y-5" data-testid="tab-personal">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <ReadOnly label="Legal name" value={p.legal_name} testId="personal-legal-name" />
        <ReadOnly label="Login email" value={p.email} testId="personal-email" />
        <ReadOnly label="Employee ID" value={p.employee_id} testId="personal-employee-id" />
        <ReadOnly label="Branch / region" value={p.branch_id} testId="personal-branch" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="text-xs text-muted-foreground space-y-1 block">Preferred name
          <Input value={form.preferred_name} onChange={(e) => setForm({ ...form, preferred_name: e.target.value })} data-testid="personal-preferred-name" aria-label="Preferred name" />
        </label>
        <label className="text-xs text-muted-foreground space-y-1 block">Phone
          <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="personal-phone" aria-label="Phone" />
        </label>
        <label className="text-xs text-muted-foreground space-y-1 block sm:col-span-2">Personal email (does not change your login)
          <Input value={form.personal_email} onChange={(e) => setForm({ ...form, personal_email: e.target.value })} data-testid="personal-personal-email" aria-label="Personal email" />
        </label>
      </div>
      <div>
        <div className="text-sm font-medium mb-2">Address</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input placeholder="Address line 1" value={form.address.line1 || ''} onChange={(e) => setForm({ ...form, address: { ...form.address, line1: e.target.value } })} data-testid="personal-address-line1" aria-label="Address line 1" />
          <Input placeholder="Address line 2" value={form.address.line2 || ''} onChange={(e) => setForm({ ...form, address: { ...form.address, line2: e.target.value } })} aria-label="Address line 2" />
          <Input placeholder="Suburb" value={form.address.suburb || ''} onChange={(e) => setForm({ ...form, address: { ...form.address, suburb: e.target.value } })} aria-label="Suburb" />
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="State" value={form.address.state || ''} onChange={(e) => setForm({ ...form, address: { ...form.address, state: e.target.value } })} aria-label="State" />
            <Input placeholder="Postcode" value={form.address.postcode || ''} onChange={(e) => setForm({ ...form, address: { ...form.address, postcode: e.target.value } })} aria-label="Postcode" />
          </div>
        </div>
      </div>
      <div>
        <div className="text-sm font-medium mb-2 flex items-center justify-between">Emergency contacts
          {form.emergency_contacts.length < 3 && (
            <Button size="sm" variant="outline" className="h-7" onClick={() => setForm({ ...form, emergency_contacts: [...form.emergency_contacts, { name: '', relationship: '', phone: '' }] })} data-testid="ec-add-btn"><Plus className="h-3.5 w-3.5 mr-1" />Add</Button>
          )}
        </div>
        {form.emergency_contacts.length === 0 && <div className="text-xs text-muted-foreground" data-testid="ec-empty">No emergency contacts recorded.</div>}
        {form.emergency_contacts.map((c, i) => (
          <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-2 mb-2 items-center">
            <Input placeholder="Name" value={c.name} onChange={(e) => setEc(i, 'name', e.target.value)} data-testid={`ec-name-${i}`} aria-label="Emergency contact name" />
            <Input placeholder="Relationship" value={c.relationship || ''} onChange={(e) => setEc(i, 'relationship', e.target.value)} aria-label="Relationship" />
            <Input placeholder="Phone" value={c.phone} onChange={(e) => setEc(i, 'phone', e.target.value)} data-testid={`ec-phone-${i}`} aria-label="Emergency contact phone" />
            <Button size="sm" variant="ghost" className="h-8" aria-label="Remove contact" onClick={() => setForm({ ...form, emergency_contacts: form.emergency_contacts.filter((_, j) => j !== i) })} data-testid={`ec-remove-${i}`}><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
          </div>
        ))}
      </div>
      <Button onClick={save} disabled={busy} data-testid="personal-save-btn">Save personal details</Button>
    </div>
  );
}

function ProfileTab({ data, version, reload }) {
  const pr = data.profile;
  const [form, setForm] = useState({ bio: pr.bio || '', skills: (pr.skills || []).join(', '), languages: (pr.languages || []).join(', ') });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put('/staff/me/profile', {
        bio: form.bio,
        skills: form.skills.split(',').map((s) => s.trim()).filter(Boolean),
        languages: form.languages.split(',').map((s) => s.trim()).filter(Boolean),
        version,
      });
      toast.success('Profile saved');
      reload();
    } catch (e) { toast.error(errMsg(e, 'Unable to save profile')); } finally { setBusy(false); }
  };
  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setBusy(true);
    try {
      await api.post('/staff/me/photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Photo updated');
      reload();
    } catch (er) { toast.error(errMsg(er, 'Unable to upload photo')); } finally { setBusy(false); e.target.value = ''; }
  };
  return (
    <div className="space-y-5" data-testid="tab-profile">
      <div className="flex items-center gap-4">
        {pr.photo_url
          ? <img src={`${process.env.REACT_APP_BACKEND_URL}${pr.photo_url}`} alt="Profile" className="h-20 w-20 rounded-full object-cover border" data-testid="profile-photo" />
          : <div className="h-20 w-20 rounded-full bg-muted flex items-center justify-center text-muted-foreground" data-testid="profile-photo-placeholder"><Camera className="h-7 w-7" /></div>}
        <label className="text-sm">
          <span className="inline-flex items-center gap-1.5 border rounded-md px-3 py-1.5 cursor-pointer hover:bg-muted text-sm"><Upload className="h-4 w-4" />Upload photo</span>
          <input type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" onChange={uploadPhoto} data-testid="profile-photo-input" aria-label="Upload profile photo" />
          <div className="text-[11px] text-muted-foreground mt-1">JPEG, PNG or WebP, up to 5MB. Stored securely.</div>
        </label>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <ReadOnly label="Role" value={roleLabel(pr.role_name)} testId="profile-role" />
        <ReadOnly label="Region / team" value={data.personal.branch_id} testId="profile-region" />
        <ReadOnly label="Commenced" value={pr.commencement} testId="profile-commencement" />
      </div>
      <label className="text-xs text-muted-foreground space-y-1 block">Professional biography
        <Textarea rows={4} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} data-testid="profile-bio" aria-label="Professional biography" />
      </label>
      <label className="text-xs text-muted-foreground space-y-1 block">Skills (comma separated)
        <Input value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} data-testid="profile-skills" aria-label="Skills" />
      </label>
      <label className="text-xs text-muted-foreground space-y-1 block">Languages (comma separated)
        <Input value={form.languages} onChange={(e) => setForm({ ...form, languages: e.target.value })} data-testid="profile-languages" aria-label="Languages" />
      </label>
      <Button onClick={save} disabled={busy} data-testid="profile-save-btn">Save profile</Button>
    </div>
  );
}

function EmploymentTab({ data }) {
  const e = data.employment;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="tab-employment">
      <ReadOnly label="Employment type" value={e.employment_type} testId="emp-type" />
      <ReadOnly label="Position / department" value={e.position} testId="emp-position" />
      <ReadOnly label="Commencement date" value={e.hire_date} testId="emp-hire-date" />
      <ReadOnly label="Probation end" value={e.probation_end_date} testId="emp-probation" />
      <ReadOnly label="Manager" value={e.manager_name} testId="emp-manager" />
      <ReadOnly label="Compliance status" value={e.compliance_status} testId="emp-compliance" />
      <ReadOnly label="Branch / team" value={e.branch_id} testId="emp-branch" />
      <ReadOnly label="State / region" value={e.state_id} testId="emp-state" />
      <div className="sm:col-span-2 text-xs text-muted-foreground">Employment details are maintained by HR. Contact your manager or HR to request corrections.</div>
    </div>
  );
}

const QUAL_BADGE = { VERIFIED: 'bg-emerald-100 text-emerald-700', PENDING_REVIEW: 'bg-amber-100 text-amber-700', EXPIRED: 'bg-red-100 text-red-700', DUE_SOON: 'bg-amber-100 text-amber-700' };

function QualificationsTab() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: '', qual_type: '', issuer: '', issue_date: '', expiry_date: '', file: null });
  const load = useCallback(() => {
    api.get('/staff/me/qualifications').then((r) => setItems(r.data.data)).catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  const submit = async () => {
    if (!form.name.trim() || !form.file) { toast.error('A name and evidence file are required'); return; }
    const fd = new FormData();
    fd.append('file', form.file);
    fd.append('name', form.name);
    if (form.qual_type) fd.append('qual_type', form.qual_type);
    if (form.issuer) fd.append('issuer', form.issuer);
    if (form.issue_date) fd.append('issue_date', form.issue_date);
    if (form.expiry_date) fd.append('expiry_date', form.expiry_date);
    setBusy(true);
    try {
      await api.post('/staff/me/qualifications/evidence', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Evidence submitted for HR review');
      setOpen(false);
      setForm({ name: '', qual_type: '', issuer: '', issue_date: '', expiry_date: '', file: null });
      load();
    } catch (e) { toast.error(errMsg(e, 'Unable to submit evidence')); } finally { setBusy(false); }
  };
  if (items === null) return <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>;
  return (
    <div className="space-y-3" data-testid="tab-qualifications">
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">Verification is completed by HR — submitted evidence shows as Pending review.</div>
        <Button size="sm" onClick={() => setOpen(true)} data-testid="qual-submit-btn"><Plus className="h-4 w-4 mr-1" />Submit evidence</Button>
      </div>
      {items.length === 0 && <div className="py-8 text-center text-sm text-muted-foreground" data-testid="quals-empty">No qualifications on record yet.</div>}
      {items.map((q) => (
        <div key={q.index} className="py-2.5 border-b last:border-0 flex flex-wrap items-center gap-2" data-testid={`qual-${q.index}`}>
          <div className="flex-1 min-w-[200px]">
            <div className="text-sm font-medium">{q.name}</div>
            <div className="text-xs text-muted-foreground">{q.issuer || 'Issuer not recorded'}{q.issue_date ? ` · issued ${q.issue_date}` : ''}{q.expiry_date ? ` · expires ${q.expiry_date}` : ' · no expiry'}</div>
          </div>
          {q.review_status && <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${QUAL_BADGE[q.review_status] || 'bg-muted text-muted-foreground'}`}>{q.review_status.replace('_', ' ')}</span>}
          {(q.status === 'EXPIRED' || q.status === 'DUE_SOON') && <Badge variant="destructive" className="text-[10px]">{q.status.replace('_', ' ')}</Badge>}
        </div>
      ))}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="qual-dialog">
          <DialogHeader><DialogTitle>Submit qualification evidence</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Qualification name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="qual-form-name" aria-label="Qualification name" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input placeholder="Type (e.g. First Aid)" value={form.qual_type} onChange={(e) => setForm({ ...form, qual_type: e.target.value })} aria-label="Qualification type" />
              <Input placeholder="Issuer" value={form.issuer} onChange={(e) => setForm({ ...form, issuer: e.target.value })} aria-label="Issuer" />
              <label className="text-xs text-muted-foreground space-y-1 block">Issue date
                <Input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} aria-label="Issue date" />
              </label>
              <label className="text-xs text-muted-foreground space-y-1 block">Expiry date
                <Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} aria-label="Expiry date" />
              </label>
            </div>
            <label className="text-xs text-muted-foreground space-y-1 block">Evidence file (PDF, JPEG or PNG, up to 10MB) *
              <Input type="file" accept="application/pdf,image/jpeg,image/png" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} data-testid="qual-form-file" aria-label="Evidence file" />
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy} data-testid="qual-form-submit">Submit for review</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DocumentsTab() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get('/staff/me/documents').then((r) => setItems(r.data.data)).catch(() => setItems([]));
  }, []);
  const open = async (d) => {
    try {
      const r = await api.get(`/staff/me/documents/${d.index}/download-url`);
      window.open(`${process.env.REACT_APP_BACKEND_URL}${r.data.data.url}`, '_blank', 'noopener');
    } catch { toast.error('Unable to open this document'); }
  };
  if (items === null) return <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>;
  if (items.length === 0) return <div className="py-8 text-center text-sm text-muted-foreground" data-testid="docs-empty">No employment documents on record yet. Documents added by HR will appear here.</div>;
  return (
    <div data-testid="tab-documents">
      {items.map((d) => (
        <div key={d.index} className="py-2.5 border-b last:border-0 flex flex-wrap items-center gap-2" data-testid={`empdoc-${d.index}`}>
          <FolderLock className="h-4 w-4 text-primary" />
          <div className="flex-1 min-w-[200px]">
            <div className="text-sm font-medium">{d.name}</div>
            <div className="text-xs text-muted-foreground">{d.uploaded_at ? `Added ${new Date(d.uploaded_at).toLocaleDateString()}` : ''}{d.expiry_date ? ` · expires ${d.expiry_date}` : ''}{d.review_status ? ` · ${d.review_status.replace('_', ' ').toLowerCase()}` : ''}</div>
          </div>
          {d.has_file && <Button size="sm" variant="outline" className="h-7" onClick={() => open(d)} data-testid={`empdoc-open-${d.index}`}>View</Button>}
        </div>
      ))}
    </div>
  );
}

export default function MyEmploymentPage() {
  const [tab, setTab] = useState('personal');
  const [state, setState] = useState({ data: null, error: null, loading: true });
  const reload = useCallback(() => {
    api.get('/staff/me/employment').then((r) => setState({ data: r.data.data, error: null, loading: false }))
      .catch((e) => setState({ data: null, error: e?.response?.status === 404 ? 'My Employment self-service is not enabled for your organisation yet.' : 'Unable to load your employment record.', loading: false }));
  }, []);
  useEffect(() => { reload(); }, [reload]);

  if (state.loading) return <div className="py-12 text-center text-sm text-muted-foreground" aria-busy="true">Loading…</div>;
  if (state.error) return <div className="py-12 text-center text-sm text-muted-foreground" data-testid="employment-unavailable">{state.error}</div>;
  const d = state.data;
  const content = {
    personal: <PersonalTab key={d.version} data={d} version={d.version} reload={reload} />,
    profile: <ProfileTab key={d.version} data={d} version={d.version} reload={reload} />,
    employment: <EmploymentTab data={d} />,
    qualifications: <QualificationsTab />,
    documents: <DocumentsTab />,
  };
  return (
    <div className="space-y-4 max-w-4xl" data-testid="my-employment-page">
      <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="My Employment sections">
        {TABS.map(([key, label, Icon]) => (
          <button key={key} role="tab" aria-selected={tab === key} onClick={() => setTab(key)}
                  data-testid={`employment-tab-${key}`}
                  className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${tab === key ? 'bg-primary text-primary-foreground border-primary' : 'bg-white text-muted-foreground border-border hover:border-primary/40'}`}>
            <Icon className="h-3.5 w-3.5" />{label}
          </button>
        ))}
      </div>
      <Card><CardContent className="pt-5">{content[tab]}</CardContent></Card>
    </div>
  );
}
